import DocIcon from '@icons/DocIcon/DocIcon';
import { formatFileSize } from '@utils/utils.ts';
import PdfIcon from '@icons/PdfIcon/PdfIcon';
import { AudioSectionFile, CaseFile } from '@api/document/model/types';
import {
  IconAlertTriangle,
  IconPhotoFilled,
  IconTrash,
} from '@tabler/icons-react';
import AudioIcon from '@icons/AudioIcon/AudioIcon';
import Tooltip from '@mui/material/Tooltip';
import {
  DocumentGeneratorInstanceCustomFieldImage,
  DocumentInstanceCustomFieldFile,
} from '@app/types/document-instance';
import AnimateButton from '@components/extended/AnimateButton';
import IconButton from '@mui/material/IconButton';
import { DocumentPromptPreviewFile } from '@app/types/document-prompt-preview';
import { useDocumentPreview } from '@components/DocumentPreviewDialog/useDocumentPreview';
import styles from './FileTag.module.scss';

/**
 * A file the server has told us it could not read any text from.
 *
 * Only an Unreadable file is badged. A readable one, and one not checked yet,
 * show nothing, so a list of twenty good files looks exactly as it does today
 * and the one that needs attention is the only one marked. Every other caller
 * of FileTag passes files without this field and renders unchanged.
 */
const unreadableDetail = (file: unknown): string | null => {
  if (!file || typeof file !== 'object') return null;
  const row = file as { text_status?: string; text_status_detail?: string };
  if (row.text_status !== 'Unreadable') return null;
  return (
    row.text_status_detail ||
    'No readable text found in this file, so nothing from it can be used.'
  );
};

interface FileTagProps {
  file:
    | File
    | CaseFile
    | SectionFile
    | AudioSectionFile
    | DocumentInstanceCustomFieldFile
    | DocumentGeneratorInstanceCustomFieldImage
    | DocumentPromptPreviewFile;
  className?: string;
  removeFile?: (file: any) => void;
}

/**
 * @param props - The props for the component.
 * @returns A React component that displays a file tag.
 */
function FileTag(props: FileTagProps) {
  const { file, className, removeFile } = props;
  const { openPreview, previewDialog } = useDocumentPreview();

  const handleRemoveFile = (
    event: React.MouseEvent<HTMLButtonElement, MouseEvent>
  ) => {
    event.stopPropagation();

    if (removeFile) {
      if (file instanceof File) {
        removeFile(file.name);
      } else {
        removeFile(file.id);
      }
    }
  };

  const renderFileIcon = () => {
    const fileName = file instanceof File ? file.name : file.filename;
    const extension = fileName?.split('.').pop()?.toLowerCase();
    switch (extension) {
      case 'doc':
      case 'docx':
        return <DocIcon />;
      case 'pdf':
        return <PdfIcon />;
      case 'mp3':
      case 'wav':
      case 'wave':
      case 'ogg':
        return <AudioIcon />;
      case 'png':
      case 'jpg':
      case 'jpeg':
        return <IconPhotoFilled color="#2479BF" size="22px" />;
      default:
        return <DocIcon />;
    }
  };

  // ZLAYT-1445: an uploaded file opens in the in-app preview, not a new tab
  // on the storage URL (QA D-21). A file not uploaded yet has no URL.
  const previewFile = (file: any) => {
    if (file instanceof File) {
      return;
    }

    openPreview(file.file || file.url || file.image, file.filename);
  };

  return (
    <div className={className || styles.fileTagBlock}>
      <div className={styles.fileTagDescBlock}>
        {renderFileIcon()}
        <div className={styles.fileTagDesc}>
          <Tooltip title={file instanceof File ? file.name : file.filename}>
            <span className={styles.title} onClick={() => previewFile(file)}>
              {file instanceof File ? file.name : file.filename}
            </span>
          </Tooltip>
          <span className={styles.size}>
            {file instanceof File ? formatFileSize(file.size) : file.size}
          </span>
        </div>
      </div>
      {unreadableDetail(file) && (
        <Tooltip title={unreadableDetail(file)} arrow>
          <span className={styles.textStatusWarning}>
            <IconAlertTriangle size={12} />
            No readable text
          </span>
        </Tooltip>
      )}
      {removeFile && (
        <AnimateButton>
          <Tooltip title="Delete file">
            <IconButton size="small" onClick={handleRemoveFile}>
              <IconTrash />
            </IconButton>
          </Tooltip>
        </AnimateButton>
      )}
      {previewDialog}
    </div>
  );
}

export { FileTag };
